import { HttpResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  UserService,
  UserGroupService,
  PermissionService,
} from '@pulpo/pulpo-api';
import {
  User,
  UserGroup,
  Permission,
  UserGroupPermissionResponseDTO,
  UserGroupAssignmentRequestDTO,
  isAgencyGroup,
} from '@pulpo/pulpo-models';
import { Observable, Subscription } from 'rxjs';

import { clearPossibleSubscriptions } from '../../../../../../../libs/pulpo-commons/src/lib/utils/subscription-helper';

@Component({
  standalone: false,
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss'],
})
export class UserEditComponent implements OnInit, OnDestroy {
  user: User;
  editForm: FormGroup;
  private resultSubscription: Subscription;
  isSaving: boolean;
  forceDelete = false;

  // Group Management Properties
  userGroups: UserGroupPermissionResponseDTO[] = [];
  userAgencies: UserGroupPermissionResponseDTO[] = [];
  userNonAgencyGroups: UserGroupPermissionResponseDTO[] = [];
  availableGroups: UserGroup[] = [];
  allPermissions: Permission[] = [];
  selectedGroupId: string | null = null;
  selectedPermissionIds: string[] = [];
  isLoadingGroups = false;
  isLoadingAvailableGroups = false;
  isAddingGroup = false;
  isRemovingGroup = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<UserEditComponent>,
    private userService: UserService,
    private userGroupService: UserGroupService,
    private permissionService: PermissionService,
    private cdr: ChangeDetectorRef
  ) {
    this.user = data.user;
  }

  ngOnInit(): void {
    this.isSaving = false;
    this.initUpdateForm();
    this.loadUserGroups();
    this.loadAvailableGroups();
    this.loadPermissions();
  }

  ngOnDestroy(): void {
    clearPossibleSubscriptions(this.resultSubscription);
  }

  initUpdateForm(): void {
    this.editForm = this.fb.group({ ...this.user });
  }

  onSubmitForm(): void {
    this.isSaving = true;

    const updatedUser = {
      ...this.user,
      ...this.editForm.value,
    };

    this.subscribeToSaveResponse(this.userService.update(updatedUser));
  }

  private subscribeToSaveResponse(result: Observable<HttpResponse<User>>) {
    clearPossibleSubscriptions(this.resultSubscription);

    this.resultSubscription = result.subscribe({
      next: (res) => {
        this.isSaving = false;
        this.cdr.markForCheck();
        this.dialogRef.close(res.body);
      },
      error: (err) => {
        console.error(err);
        this.isSaving = false;
        this.cdr.markForCheck();
      },
    });
  }

  private subscribeToDeleteResponse(result: Observable<HttpResponse<any>>) {
    clearPossibleSubscriptions(this.resultSubscription);

    this.resultSubscription = result.subscribe({
      next: () => {
        this.isSaving = false;
        this.cdr.markForCheck();
        this.dialogRef.close(-1);
      },
      error: (err) => {
        console.error(err);
        this.isSaving = false;
        this.cdr.markForCheck();
      },
    });
  }

  askDelete() {
    this.forceDelete = true;
  }

  confirmDeletion() {
    this.isSaving = true;
    this.subscribeToDeleteResponse(this.userService.delete(this.user.id));
  }

  // ==================== GROUP MANAGEMENT METHODS ====================

  /**
   * Load user's current groups with permissions
   */
  private loadUserGroups(): void {
    this.isLoadingGroups = true;
    this.cdr.markForCheck();
    this.userGroupService.getUserGroups(this.user.id).subscribe({
      next: (res: HttpResponse<UserGroupPermissionResponseDTO[]>) => {
        if (res.body) {
          this.userGroups = res.body;
          // Separate agencies from non-agency groups, per the user service
          // `types` carried by the group definition
          const isAgency = (g: UserGroupPermissionResponseDTO) =>
            isAgencyGroup(
              this.availableGroups.find((ag: UserGroup) => ag.id === g.groupId)
            );
          this.userAgencies = this.userGroups.filter(isAgency);
          this.userNonAgencyGroups = this.userGroups.filter(
            (g: UserGroupPermissionResponseDTO) => !isAgency(g)
          );
        }
        this.isLoadingGroups = false;
      },
      error: (err) => {
        console.error('Failed to load user groups:', err);
        this.isLoadingGroups = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadAvailableGroups(): void {
    this.isLoadingAvailableGroups = true;
    this.userGroupService.getAvailableGroups().subscribe({
      next: (res: HttpResponse<UserGroup[]>) => {
        if (res.body) {
          // Store ALL groups to properly identify agencies when loading user groups
          this.availableGroups = res.body;
        }
        this.isLoadingAvailableGroups = false;
        // Reload user groups to properly separate agencies/non-agencies
        this.loadUserGroups();
      },
      error: (err) => {
        console.error('Failed to load available groups:', err);
        this.isLoadingAvailableGroups = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Load all permissions for the dropdown
   */
  private loadPermissions(): void {
    this.permissionService.getAllPermissions().subscribe({
      next: (res: HttpResponse<Permission[]>) => {
        if (res.body) {
          this.allPermissions = res.body;
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to load permissions:', err);
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Handle group selection from dropdown
   */
  onGroupSelected(groupId: string): void {
    this.selectedGroupId = groupId;
    this.selectedPermissionIds = [];
  }

  /**
   * Toggle permission selection
   */
  togglePermission(permissionId: string): void {
    const index = this.selectedPermissionIds.indexOf(permissionId);
    if (index > -1) {
      this.selectedPermissionIds.splice(index, 1);
    } else {
      this.selectedPermissionIds.push(permissionId);
    }
  }

  /**
   * Check if a permission is selected
   */
  isPermissionSelected(permissionId: string): boolean {
    return this.selectedPermissionIds.includes(permissionId);
  }

  /**
   * Add selected group with permissions to the user
   */
  addGroupToUser(): void {
    if (!this.selectedGroupId || this.selectedPermissionIds.length === 0) {
      console.warn('Please select a group and at least one permission');
      return;
    }

    // Check if user is already in the group
    const existingGroup = this.userGroups.find(
      (g) => g.groupId === this.selectedGroupId
    );
    if (existingGroup) {
      console.warn('User is already a member of this group');
      return;
    }

    this.isAddingGroup = true;
    this.cdr.markForCheck();

    const request = new UserGroupAssignmentRequestDTO();
    request.userId = this.user.id;
    request.groupId = this.selectedGroupId;
    request.permissionIds = this.selectedPermissionIds;

    this.userGroupService.addUserToGroup(this.user.id, request).subscribe({
      next: () => {
        console.log('User added to group successfully');
        this.selectedGroupId = null;
        this.selectedPermissionIds = [];
        this.loadUserGroups();
        this.isAddingGroup = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to add user to group:', err);
        this.isAddingGroup = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Remove user from a group
   */
  removeGroupFromUser(groupId: string): void {
    if (
      !confirm('Are you sure you want to remove this user from this group?')
    ) {
      return;
    }

    this.isRemovingGroup = true;
    this.cdr.markForCheck();

    this.userGroupService.removeUserFromGroup(this.user.id, groupId).subscribe({
      next: () => {
        console.log('User removed from group successfully');
        this.loadUserGroups();
        this.isRemovingGroup = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to remove user from group:', err);
        this.isRemovingGroup = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Check if can add more groups (user not already in selected group)
   */
  canAddGroup(): boolean {
    return (
      !!this.selectedGroupId &&
      this.selectedPermissionIds.length > 0 &&
      !this.userGroups.some((g) => g.groupId === this.selectedGroupId)
    );
  }

  /**
   * Get permission name by ID
   */
  getPermissionName(permissionId: string): string {
    return (
      this.allPermissions.find((p) => p.id === permissionId)?.name ||
      permissionId
    );
  }

  /**
   * Get group name by ID
   */
  getGroupName(groupId: string): string {
    return this.availableGroups.find((g) => g.id === groupId)?.name || groupId;
  }
}
